import React, { useMemo, useCallback } from 'react';
import { Flex, Text, Box, Link } from 'rebass/styled-components';
import { Loader } from '@getro/rombo';
import { Linkedin, FileText, LinkIcon } from 'lucide-react';
import { ContactEmailDropdown } from 'components/molecules/contactEmailDropdown';
import PropTypes from 'prop-types';
import { addProtocolToUrl } from 'helpers/urlHelpers';
import { useSecurePdfOpener } from 'hooks/useSecurePdfOpener';
import DynamicTooltip from 'components/atoms/dynamicTooltip';
import { SourceDetails } from './sourceDetails';

export const ContactSocialsAndSource = ({
  contact,
  showEmailDropdown,
  showJobSeeking,
  trackLinkedinClick,
  onLinkedinClick,
}) => {
  const { openSecurePdf, isLoading } = useSecurePdfOpener();

  const applicationResumeSources = useMemo(() => {
    if (contact.resumeUrl || !showJobSeeking) {
      return [];
    }

    return (contact.jobApplications ?? [])
      .filter((application) => Boolean(application?.cvUrl))
      .map((application) => ({
        source: 'job_application_resume',
        title: application.job?.title || 'job application',
        url: application.cvUrl,
        addedOn: application.createdAt,
      }));
  }, [contact.resumeUrl, contact.jobApplications, showJobSeeking]);

  const onClickResume = useCallback(
    (event) => {
      event.preventDefault();
      event.stopPropagation();

      if (!isLoading) {
        openSecurePdf(contact.id);
      }
    },
    [contact?.id, isLoading, openSecurePdf],
  );

  const onLinkedinVisit = useCallback(
    (event) => {
      event.stopPropagation();

      trackLinkedinClick();

      if (onLinkedinClick) {
        onLinkedinClick();
      }
    },
    [trackLinkedinClick, onLinkedinClick],
  );

  const items = useMemo(
    () =>
      [
        {
          icon: Linkedin,
          url: contact?.linkedinUrl,
          tooltip: 'Open LinkedIn profile',
          onClick: onLinkedinVisit,
          show: Boolean(contact.linkedinUrl),
        },
        {
          icon: LinkIcon,
          url: addProtocolToUrl(contact.websiteUrl),
          tooltip: 'View website',
          onClick: (event) => event.stopPropagation(),
          show: Boolean(contact.websiteUrl),
        },
        {
          icon: isLoading ? Loader : FileText,
          url: contact.resumeUrl,
          tooltip: 'View resume. 🔒 This file has been scanned and is safe to open',
          onClick: onClickResume,
          props: {
            size: isLoading ? 'small' : '16px',
          },
          show: Boolean(contact.resumeUrl),
        },
        {
          icon: ContactEmailDropdown,
          url: contact.email,
          tooltip: null,
          props: {
            contact,
            showTooltip: true,
          },
          show: showEmailDropdown,
        },
        {
          icon: SourceDetails,
          url: applicationResumeSources,
          tooltip: null,
          onClick: (event) => event.stopPropagation(),
          show: applicationResumeSources.length > 0,
          props: {
            sources: applicationResumeSources,
            label: 'Resume',
            triggerIcon: FileText,
          },
        },
        {
          icon: SourceDetails,
          url: contact.sources,
          tooltip: null,
          onClick: (event) => event.stopPropagation(),
          show: true,
          props: {
            sources: contact.sources,
          },
        },
      ].filter((item) => item.show),
    [contact, onLinkedinVisit, isLoading, onClickResume, showEmailDropdown, applicationResumeSources],
  );

  return (
    <Flex sx={{ flex: '0 0 auto' }}>
      <Flex
        className="contact-avatar-socials"
        sx={{
          alignItems: 'center',
          gap: '16px',
          rowGap: '8px',
          columnGap: '16px',
          flexDirection: 'row',
          flexWrap: 'wrap',
        }}
      >
        {items.map((item) => (
          <DynamicTooltip
            position="top-end"
            content={
              <Text fontSize="14px" color="neutral.0">
                {item.tooltip}
              </Text>
            }
            trigger={
              item.tooltip ? (
                <Flex
                  as={Link}
                  color="text.main"
                  flex="0 0 auto"
                  sx={{ borderRadius: 'default', '&:hover': { bg: 'neutral.20' } }}
                  alignItems="center"
                  justifyContent="center"
                  href={item.url}
                  rel="noopener noreferrer"
                  target="_blank"
                  onClick={item.onClick}
                >
                  <Box as={item.icon} size={16} sx={{ color: 'text.subtle' }} {...item.props} />
                </Flex>
              ) : (
                <Box as={item.icon} {...item.props} />
              )
            }
          />
        ))}
      </Flex>
    </Flex>
  );
};

ContactSocialsAndSource.propTypes = {
  contact: PropTypes.object.isRequired,
  showEmailDropdown: PropTypes.bool,
  showJobSeeking: PropTypes.bool,
  trackLinkedinClick: PropTypes.func,
  onLinkedinClick: PropTypes.func,
};

ContactSocialsAndSource.defaultProps = {
  showEmailDropdown: false,
  showJobSeeking: false,
  trackLinkedinClick: () => {},
  onLinkedinClick: () => {},
};
